import { ApiClientError, api, unwrap } from "@lumberset/api-client";
import { accountCreditNote, date, LedgerStatus, money } from "@lumberset/takeoff-ui";
import {
  Button,
  Container,
  EmptyState,
  Field,
  Input,
  Prompt,
  SectionHeader,
  SectionRow,
  Table,
  toast,
} from "@lumberset/ui";
import { CreditCard, ExclamationCircle } from "@medusajs/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useEffect, useState } from "react";
import {
  AddCardDialog,
  type BillingSummary,
  price,
  useCardReturn,
} from "../../../components/add-card";
import { OfferCard } from "../../../components/billing-offer";
import {
  CUSTOM_PLAN_FROM,
  CustomPlanCard,
  CustomPlanRequestDrawer,
  RequestSent,
} from "../../../components/custom-plan";
import { billingOffersQuery, billingQuery, creditPacksQuery } from "../../../lib/queries";

interface BillingSearch {
  /** Back from Stripe's card page. */
  card?: "saved" | "canceled";
  /** The card was added to accept an offer. */
  resume?: "offer";
}

export const Route = createFileRoute("/_app/settings/billing")({
  validateSearch: (search: Record<string, unknown>): BillingSearch => ({
    ...(search.card === "saved" || search.card === "canceled" ? { card: search.card } : {}),
    ...(search.resume === "offer" ? { resume: "offer" as const } : {}),
  }),
  component: Billing,
});

function Billing() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { yard } = Route.useRouteContext();
  // Only an Owner handles the card; an Owner or a Manager decides on the plan.
  const isOwner = yard.role === "owner";
  const decidesPlan = isOwner || yard.role === "admin";

  const { data, error, isPending } = useQuery(billingQuery);
  const { data: packs } = useQuery(creditPacksQuery);
  const { data: offers } = useQuery({ ...billingOffersQuery, enabled: decidesPlan });
  const [code, setCode] = useState("");
  const [cancelingPlan, setCancelingPlan] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [askingOwner, setAskingOwner] = useState(false);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["billing"] });
  const clearSearch = () => navigate({ to: "/settings/billing", search: {}, replace: true });

  // Back from Stripe: wait for the card to be recorded, then say so.
  const cardReturn = useCardReturn({
    active: search.card !== undefined,
    card: search.card,
    onReady: () => {
      void refresh();
      toast.success("Card saved", {
        description:
          search.resume === "offer"
            ? "You can accept the plan now."
            : "Takeoffs start the moment you upload.",
      });
      void clearSearch();
    },
    onCanceled: () => {
      toast.info("No card was added", { description: "Nothing changed." });
      void clearSearch();
    },
  });

  // Coming back to accept an offer: bring it into view.
  const firstOffer = offers?.offers[0];
  useEffect(() => {
    if (search.resume !== "offer" || !firstOffer) return;
    document.getElementById(`offer-${firstOffer.id}`)?.scrollIntoView({ block: "center" });
  }, [search.resume, firstOffer]);

  const openStripe = useMutation({
    mutationFn: (kind: "card-setup" | "portal") =>
      unwrap(
        kind === "portal"
          ? api.billing.portal.$post()
          : api.billing["card-setup"].$post({ json: { returnTo: "/settings/billing" } }),
      ),
    onSuccess: ({ url }) => window.location.assign(url),
    onError: (failure) => toast.error(failure.message),
  });

  const redeem = useMutation({
    mutationFn: (value: string) =>
      unwrap(api.billing.coupons.redeem.$post({ json: { code: value } })),
    onSuccess: async (result) => {
      await refresh();
      setCode("");
      toast.success(
        result.creditsAdded > 0
          ? `${result.creditsAdded} ${result.creditsAdded === 1 ? "credit" : "credits"} added`
          : `Coupon ${result.coupon.code} applied`,
        {
          description:
            result.creditsAdded > 0
              ? `You now have ${result.creditQuotes} ${result.creditQuotes === 1 ? "credit" : "credits"}.`
              : result.discount
                ? `${discountWords(result.discount)}.`
                : undefined,
        },
      );
    },
    onError: (failure) => toast.error(failure.message),
  });

  const buy = useMutation({
    mutationFn: (packId: string) =>
      unwrap(api.billing["credit-packs"][":id"].buy.$post({ param: { id: packId } })),
    onSuccess: async (result) => {
      await refresh();
      toast.success(`${result.creditsAdded} credits added`, {
        description: "They cover your next takeoffs before any fee is charged.",
      });
    },
    onError: (failure) => toast.error(failure.message),
  });

  const cancelPlan = useMutation({
    mutationFn: () => unwrap(api.billing.plan.cancel.$post()),
    onSuccess: async (result) => {
      setCancelingPlan(false);
      await refresh();
      toast.success(`${result.planName} is canceled`, {
        description: result.endsAt
          ? `It stays on until ${date(result.endsAt)}. Nothing more is charged.`
          : "Nothing more is charged.",
      });
    },
    onError: (failure) => toast.error(failure.message),
  });

  const keepPlan = useMutation({
    mutationFn: () => unwrap(api.billing.plan.keep.$post()),
    onSuccess: async (result) => {
      await refresh();
      toast.success(`${result.planName} stays on`, {
        description: result.renewsAt ? `It renews on ${date(result.renewsAt)}.` : undefined,
      });
    },
    onError: (failure) => toast.error(failure.message),
  });

  const pay = useMutation({
    mutationFn: (entryId: string) =>
      unwrap(api.billing.ledger[":id"].pay.$post({ param: { id: entryId } })),
    onSuccess: async ({ payment }) => {
      await refresh();
      if (payment.status === "paid") {
        toast.success("Paid", { description: "Thank you. Takeoffs can start again." });
      } else if (payment.status === "failed") {
        toast.error("The card was declined again", {
          description: `${payment.reason} Update the card, then try again.`,
        });
      } else {
        toast.info("Sent to your card's bank", {
          description: "It shows as paid here once it clears.",
        });
      }
    },
    onError: (failure) => {
      if (failure instanceof ApiClientError && failure.code === "payment_method_required") {
        openStripe.mutate("card-setup");
        return;
      }
      toast.error(failure.message);
    },
  });

  if (error instanceof ApiClientError && error.status === 403) {
    return (
      <Container className="p-0">
        <EmptyState icon={<CreditCard />} title="Billing is managed by your yard's Owner" />
      </Container>
    );
  }
  if (isPending || !data) return null;

  const { summary, ledger } = data;
  const next = nextTakeoffWords(summary.nextQuote, summary.takeoffsUnderWay);

  // Buying needs a card when payments are live; in development the credits are added at once.
  const canBuy = !summary.paymentsLive || summary.hasPaymentMethod;
  const monthlyPlan = summary.billingPlan.monthlyFeeCents > 0;
  const { customPlan } = summary;
  const waitingOffers = offers?.offers ?? [];
  const failed = ledger.filter((entry) => entry.status === "failed" && entry.amountCents > 0);
  // The quiet mention, for everyone not already talking to us about a custom plan.
  const mentionCustomPlan =
    !monthlyPlan && !customPlan.nudge && !customPlan.request && !customPlan.offer;

  function submitCoupon(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (code.trim()) redeem.mutate(code.trim());
  }

  return (
    <>
      {cardReturn.waiting ? (
        <Container className="txt-small px-6 py-4 text-ui-fg-subtle">
          {cardReturn.timedOut
            ? "We have not seen the card yet. It can take a minute to come through from Stripe; this page updates on its own."
            : "Saving your card. This takes a few seconds."}
        </Container>
      ) : null}

      {failed.length > 0 ? (
        <Container className="divide-y p-0">
          <div className="flex items-start gap-x-3 bg-ui-tag-red-bg px-6 py-4 text-ui-tag-red-text">
            <ExclamationCircle className="mt-0.5 shrink-0" />
            <div className="txt-small min-w-0">
              <p className="txt-compact-small-plus">
                {failed.length === 1
                  ? "A payment did not go through"
                  : `${failed.length} payments did not go through`}
              </p>
              <p className="mt-0.5 text-pretty">
                New takeoffs wait until it is paid.{" "}
                {isOwner
                  ? "Update the card if it has changed, then pay."
                  : "Ask an Owner to update the card and pay it here."}
              </p>
            </div>
          </div>
          {failed.map((entry) => (
            <div
              key={entry.id}
              className="flex flex-col gap-3 px-6 py-4 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <p className="txt-compact-small-plus text-pretty break-words text-ui-fg-base">
                  {entry.description}
                </p>
                <p className="txt-compact-xsmall mt-0.5 text-pretty text-ui-fg-muted">
                  {money(entry.amountCents, entry.currency)}, {date(entry.createdAt)}
                  {entry.failureReason ? `. ${entry.failureReason}` : ""}
                </p>
              </div>
              {isOwner && summary.paymentsLive ? (
                <div className="flex shrink-0 flex-wrap gap-2">
                  <Button
                    size="small"
                    variant="secondary"
                    isLoading={openStripe.isPending}
                    onClick={() =>
                      openStripe.mutate(summary.hasPaymentMethod ? "portal" : "card-setup")
                    }
                  >
                    {summary.hasPaymentMethod ? "Update card" : "Add a card"}
                  </Button>
                  {summary.hasPaymentMethod ? (
                    <Button
                      size="small"
                      isLoading={pay.isPending && pay.variables === entry.id}
                      disabled={pay.isPending}
                      onClick={() => pay.mutate(entry.id)}
                    >
                      Pay {price(entry.amountCents)} now
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>
          ))}
        </Container>
      ) : null}

      {waitingOffers.map((offer) => (
        <OfferCard
          key={offer.id}
          offer={offer}
          usage={offers?.usage ?? customPlan.usage}
          summary={summary}
          highlight={search.resume === "offer"}
        />
      ))}

      <Container className="divide-y p-0">
        <SectionHeader
          title={summary.billingPlan.name}
          description={
            monthlyPlan
              ? "A monthly fee with takeoffs included. Editing and re-pricing a quote is always free."
              : "You pay when a takeoff is released to you. Editing and re-pricing its quote is always free."
          }
          actions={
            summary.paymentsLive ? (
              isOwner ? (
                <Button
                  size="small"
                  variant="secondary"
                  isLoading={openStripe.isPending}
                  onClick={() =>
                    openStripe.mutate(summary.hasPaymentMethod ? "portal" : "card-setup")
                  }
                >
                  <CreditCard />{" "}
                  {summary.hasPaymentMethod ? "Manage card and receipts" : "Add a card"}
                </Button>
              ) : !summary.hasPaymentMethod ? (
                <Button size="small" variant="secondary" onClick={() => setAskingOwner(true)}>
                  <CreditCard /> Add a card
                </Button>
              ) : null
            ) : null
          }
        />
        {monthlyPlan ? (
          <>
            <SectionRow label="Monthly fee">
              {money(summary.billingPlan.monthlyFeeCents)} a month, includes{" "}
              {summary.billingPlan.includedQuotes}{" "}
              {summary.billingPlan.includedQuotes === 1 ? "takeoff" : "takeoffs"}
            </SectionRow>
            <SectionRow label="This month">
              {summary.quotesThisPeriod} of {summary.billingPlan.includedQuotes} included takeoffs
              used
            </SectionRow>
          </>
        ) : null}
        <SectionRow label={monthlyPlan ? "After that, per takeoff" : "Fee per quote"}>
          {money(summary.billingPlan.quoteFeeCents)}
        </SectionRow>
        <SectionRow label="Fee per change order">
          {summary.billingPlan.changeOrderFeeCents === 0
            ? "No charge"
            : `${money(summary.billingPlan.changeOrderFeeCents)}, when revised drawings are read again`}
        </SectionRow>
        {monthlyPlan && summary.periodEnd ? (
          <SectionRow label={summary.cancelAtPeriodEnd ? "Ends" : "Renews"}>
            {summary.cancelAtPeriodEnd
              ? date(summary.periodEnd)
              : `${date(summary.periodEnd)}, at ${money(summary.billingPlan.monthlyFeeCents)}`}
          </SectionRow>
        ) : null}
        <SectionRow label="Trial quotes left">{summary.trialQuotesRemaining}</SectionRow>
        <SectionRow label="Credits">
          {summary.creditQuotes === 0
            ? "None. Buy a pack below, or redeem a coupon."
            : `${summary.creditQuotes} ${summary.creditQuotes === 1 ? "takeoff" : "takeoffs"} prepaid`}
        </SectionRow>
        {summary.accountCreditCents > 0 ? (
          <SectionRow label="Account credit">
            {money(summary.accountCreditCents)} account credit, used before your card
          </SectionRow>
        ) : null}
        <SectionRow label="Coupon">
          {summary.discount
            ? `${summary.discount.code}: ${discountWords(summary.discount)}`
            : "None"}
        </SectionRow>
        <SectionRow label="Your next takeoff">{next}</SectionRow>
        <SectionRow label="Card on file">
          {summary.hasPaymentMethod
            ? "Yes"
            : !summary.paymentsLive
              ? "Not needed yet"
              : summary.accountCreditCents > 0
                ? "Not yet. Your account credit pays first; you will need one once it runs out."
                : "Not yet. You will need one after your trial quote."}
        </SectionRow>
        {summary.status === "past_due" && failed.length === 0 ? (
          <div className="txt-small bg-ui-tag-red-bg px-6 py-3 text-ui-tag-red-text">
            A payment failed. Update your card to run new takeoffs.
          </div>
        ) : null}
        {monthlyPlan && summary.cancelAtPeriodEnd && summary.periodEnd ? (
          <div className="flex flex-col gap-3 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="txt-small text-pretty text-ui-fg-subtle">
              Your plan ends on {date(summary.periodEnd)}; after that, takeoffs are{" "}
              {price(summary.standardFees.quoteFeeCents)} each and change orders{" "}
              {price(summary.standardFees.changeOrderFeeCents)}.
            </p>
            {decidesPlan ? (
              <Button
                size="small"
                className="shrink-0 self-start sm:self-auto"
                isLoading={keepPlan.isPending}
                onClick={() => keepPlan.mutate()}
              >
                Keep my plan
              </Button>
            ) : null}
          </div>
        ) : monthlyPlan && decidesPlan ? (
          <div className="flex justify-end px-6 py-3">
            <Button size="small" variant="transparent" onClick={() => setCancelingPlan(true)}>
              Cancel plan
            </Button>
          </div>
        ) : null}
      </Container>

      {customPlan.request ? (
        <RequestSent phone={customPlan.request.phone} />
      ) : (
        <CustomPlanCard summary={summary} />
      )}

      <Container className="divide-y p-0">
        <SectionHeader
          title="Redeem a coupon"
          description="A code from LumberSet: credits, or money off your next quote fees. One use per workspace."
        />
        <form
          onSubmit={submitCoupon}
          className="flex flex-col gap-3 px-6 py-5 sm:flex-row sm:items-end"
        >
          <div className="sm:max-w-xs sm:flex-1">
            <Field label="Coupon code">
              {(control) => (
                <Input
                  {...control}
                  value={code}
                  onChange={(event) => setCode(event.target.value.toUpperCase())}
                  placeholder="WELCOME50"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  maxLength={40}
                />
              )}
            </Field>
          </div>
          <Button
            type="submit"
            variant="secondary"
            isLoading={redeem.isPending}
            disabled={!code.trim()}
            className="max-sm:w-full"
          >
            Redeem
          </Button>
        </form>
      </Container>

      <Container className="divide-y p-0">
        <SectionHeader
          title="Buy credits"
          description="The self-serve discount: prepay takeoffs at a lower price. A credit covers one released takeoff, after any trial quotes and before the quote fee."
        />
        {!packs || packs.packs.length === 0 ? (
          <EmptyState className="py-10" title="No credit packs on sale right now" />
        ) : (
          <div className="grid gap-3 px-6 py-5 sm:grid-cols-3">
            {packs.packs.map((pack) => {
              const each = Math.round(pack.priceCents / pack.quotes);
              const saving = summary.billingPlan.quoteFeeCents * pack.quotes - pack.priceCents;
              return (
                <div
                  key={pack.id}
                  className="flex flex-col gap-y-3 rounded-lg bg-ui-bg-component p-4 shadow-borders-base"
                >
                  <div>
                    <div className="txt-compact-small-plus text-ui-fg-base">{pack.name}</div>
                    <div className="txt-compact-xsmall text-ui-fg-muted">
                      {money(each)} per takeoff
                      {saving > 0 ? `, save ${money(saving)}` : ""}
                    </div>
                  </div>
                  <div className="tabular text-[1.375rem] font-medium leading-7 text-ui-fg-base">
                    {money(pack.priceCents)}
                  </div>
                  <Button
                    size="small"
                    variant="secondary"
                    disabled={!canBuy}
                    isLoading={buy.isPending && buy.variables === pack.id}
                    onClick={() => buy.mutate(pack.id)}
                  >
                    Buy {pack.quotes} credits
                  </Button>
                </div>
              );
            })}
          </div>
        )}
        <div className="txt-small px-6 py-3 text-ui-fg-muted">
          {summary.paymentsLive
            ? summary.hasPaymentMethod
              ? "Charged to the card on file. The credits are added the moment the payment goes through."
              : "Add a card first: packs are charged to the card on file."
            : "Payments are not switched on in this environment. In development the credits are added at once and the purchase is recorded below."}
        </div>
        {mentionCustomPlan ? (
          <div className="flex flex-col gap-3 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="txt-small text-pretty text-ui-fg-subtle">
              Running {CUSTOM_PLAN_FROM} or more takeoffs or change orders a month? A custom monthly
              plan can cost less than credits. Tell us about your work and we will call you with a
              price.
            </p>
            <Button
              size="small"
              variant="secondary"
              className="shrink-0 self-start sm:self-auto"
              onClick={() => setRequesting(true)}
            >
              Request a custom plan
            </Button>
          </div>
        ) : null}
      </Container>

      <Container className="divide-y p-0">
        <SectionHeader title="History" />
        {ledger.length === 0 ? (
          <EmptyState
            className="py-10"
            title="Nothing yet"
            description="Charges, credits and coupons appear here."
          />
        ) : (
          <Table>
            <Table.Body>
              {ledger.map((entry) => (
                <Table.Row key={entry.id}>
                  <Table.Cell>
                    <div className="txt-compact-small text-ui-fg-base">{entry.description}</div>
                    <div className="txt-compact-xsmall text-ui-fg-muted">
                      {date(entry.createdAt)}
                      {entry.couponId ? ", with a coupon" : ""}
                      {entry.accountCreditCents !== 0
                        ? `. ${accountCreditNote(entry.accountCreditCents, entry.currency)}`
                        : ""}
                    </div>
                  </Table.Cell>
                  <Table.Cell className="w-[1%] whitespace-nowrap">
                    <LedgerStatus status={entry.status} />
                  </Table.Cell>
                  <Table.Cell className="tabular w-[1%] whitespace-nowrap text-right text-ui-fg-base">
                    {money(entry.amountCents, entry.currency)}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Container>

      <CustomPlanRequestDrawer
        open={requesting}
        onOpenChange={setRequesting}
        usage={customPlan.usage}
      />

      <AddCardDialog
        open={askingOwner}
        onOpenChange={setAskingOwner}
        purpose={{ kind: "next", amountCents: nextFee(summary) }}
        returnTo="/settings/billing"
      />

      <Prompt open={cancelingPlan} onOpenChange={setCancelingPlan}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Cancel {summary.billingPlan.name}?</Prompt.Title>
            <Prompt.Description>
              {summary.periodEnd
                ? `It stays on until ${date(summary.periodEnd)}, the end of the month you paid for. Nothing more is charged.`
                : "Nothing more is charged."}{" "}
              After that, takeoffs are {price(summary.standardFees.quoteFeeCents)} each and change
              orders {price(summary.standardFees.changeOrderFeeCents)}. You can keep the plan any
              time before then.
            </Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Keep my plan</Prompt.Cancel>
            <Button
              size="small"
              variant="danger"
              isLoading={cancelPlan.isPending}
              onClick={() => cancelPlan.mutate()}
            >
              Cancel plan
            </Button>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </>
  );
}

/** What the next takeoff would cost once the trial and Credits are used: the fee to name when asking for a card. */
function nextFee(summary: BillingSummary) {
  return summary.nextQuote.amountCents > 0
    ? summary.nextQuote.amountCents
    : summary.billingPlan.quoteFeeCents;
}

/**
 * The next takeoff in words: free, or its fee with what a Coupon took off and
 * what account credit pays of it, counted after any takeoffs already under way.
 */
function nextTakeoffWords(next: BillingSummary["nextQuote"], underWay: number): string {
  const words = nextTakeoffCost(next);
  if (underWay === 0) return words;
  return `${words}, after the ${underWay === 1 ? "takeoff" : `${underWay} takeoffs`} already under way`;
}

function nextTakeoffCost(next: BillingSummary["nextQuote"]): string {
  if (next.basis === "trial") return "Free, it uses a trial quote";
  if (next.basis === "credit") return "Free, it uses a credit";
  if (next.basis === "included") return "Included in your monthly fee";
  const owed = next.amountCents + next.fromAccountCreditCents;
  const coupon = next.couponId
    ? `, ${money(owed)} with your coupon instead of ${money(next.listAmountCents)}`
    : "";
  if (next.fromAccountCreditCents > 0 && next.amountCents === 0) {
    return `Paid from your account credit${coupon || `, ${money(owed)}`}`;
  }
  if (next.fromAccountCreditCents > 0) {
    return `${money(next.amountCents)} to your card, after ${money(next.fromAccountCreditCents)} of account credit${coupon}`;
  }
  return next.couponId
    ? `${money(next.amountCents)} with your coupon, instead of ${money(next.listAmountCents)}`
    : money(next.amountCents);
}

/** "50% off your next 2 quote fees", "$50.00 off your next quote fee". */
function discountWords(discount: {
  percentOff?: number;
  amountOffCents?: number;
  remainingUses: number;
}) {
  const amount =
    discount.percentOff !== undefined
      ? `${discount.percentOff}% off`
      : `${money(discount.amountOffCents ?? 0)} off`;
  return discount.remainingUses === 1
    ? `${amount} your next quote fee`
    : `${amount} your next ${discount.remainingUses} quote fees`;
}
